import { ChevronDown, ChevronRight, Trash2 } from "lucide-react";
import { useState } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "components/ui/button";
import {
  useDiscardDepositDraftMutation,
  useGetDepositDraftDetailQuery,
} from "@/services/deposit-reconciliation";
import { toast } from "@/components/ui/use-toast";
import { formatToBRL } from "@/utils/formatToMoney";

import type { IApiError, IDepositDraftSummary } from "src/@types/deposit-reconciliation";

import { MESSAGES, TEXTS } from "../../utils/messages";

interface DraftListProps {
  drafts: IDepositDraftSummary[];
  isLoading: boolean;
  onDiscarded?: () => void;
}

function DraftRow({
  draft,
  onDiscarded,
}: {
  draft: IDepositDraftSummary;
  onDiscarded?: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [discardDraft, { isLoading: discarding }] =
    useDiscardDepositDraftMutation();
  const { data: detail, isFetching: loadingDetail } =
    useGetDepositDraftDetailQuery(draft.id, { skip: !expanded });

  const handleDelete = async () => {
    try {
      await discardDraft(draft.id).unwrap();
      toast(MESSAGES.DRAFT.DISCARDED(draft.id));
      onDiscarded?.();
    } catch (err) {
      toast(MESSAGES.DRAFT.DISCARD_ERROR(err as IApiError));
    } finally {
      setConfirmOpen(false);
    }
  };

  return (
    <div className="border border-yellow-200 bg-yellow-50 rounded-md">
      <div className="flex items-center justify-between p-2 gap-2">
        <button
          className="flex items-center gap-2 text-xs text-yellow-900 hover:underline flex-1 text-left"
          onClick={() => setExpanded(e => !e)}
        >
          {expanded ? (
            <ChevronDown className="w-3 h-3" />
          ) : (
            <ChevronRight className="w-3 h-3" />
          )}
          <span className="font-semibold">{TEXTS.DRAFT.TITLE(draft.id)}</span>
          <span className="text-yellow-700">
            {TEXTS.DRAFT.SUMMARY(
              draft.nsuCount,
              draft.depositCount,
              formatToBRL(draft.totalAmount),
            )}
          </span>
        </button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-rose-600 hover:bg-rose-100 hover:text-rose-700"
          onClick={() => setConfirmOpen(true)}
          disabled={discarding}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>

      {expanded && (
        <div className="border-t border-yellow-200 p-2 text-xs">
          {loadingDetail ? (
            <div className="text-yellow-700">{TEXTS.COMMON.LOADING}</div>
          ) : detail ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <div className="text-yellow-900 font-semibold mb-1">
                  {TEXTS.DRAFT.NSU_SECTION}
                </div>
                <ul className="space-y-0.5">
                  {detail.nsus.map(n => (
                    <li key={n.id} className="font-mono text-[11px]">
                      {n.number} —{" "}
                      {formatToBRL(
                        n.adjustmentAmount !== 0 ? n.adjustmentAmount : n.amount,
                      )}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-yellow-900 font-semibold mb-1">
                  {TEXTS.DRAFT.DEPOSIT_SECTION}
                </div>
                <ul className="space-y-0.5">
                  {detail.deposits.map(d => (
                    <li key={d.id} className="font-mono text-[11px]">
                      {d.bankName} — {formatToBRL(d.amount)}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : null}
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {TEXTS.DRAFT.DISCARD_TITLE(draft.id)}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {TEXTS.DRAFT.DISCARD_DESCRIPTION(
                draft.nsuCount,
                draft.depositCount,
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{TEXTS.DRAFT.CANCEL}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={discarding}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              {TEXTS.DRAFT.DISCARD}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function DraftList({
  drafts,
  isLoading,
  onDiscarded,
}: DraftListProps) {
  if (isLoading) {
    return (
      <div className="text-xs text-slate-500 text-center py-3">
        {TEXTS.DRAFT.LOADING}
      </div>
    );
  }

  if (drafts.length === 0) {
    return (
      <div className="text-xs text-slate-500 text-center py-3">
        {TEXTS.DRAFT.EMPTY}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {drafts.map(draft => (
        <DraftRow key={draft.id} draft={draft} onDiscarded={onDiscarded} />
      ))}
    </div>
  );
}
